/**
 * Memory scanner — LLM-as-judge composition.
 *
 * Prompt construction, redaction pipeline, verdict mapping, and score
 * parsing all live in the Rust core (`memory_scan` module). This module
 * composes those with `Atbash.judgeAction` for HTTP transport.
 *
 * The entry is fenced in the judge prompt so an attacker can't
 * meta-inject into the scanner itself.
 */
import { Atbash } from "../client.js";
import { native } from "../native.js";
import type {
  AgentAuth,
  MemoryEntry,
  MemoryScanOptions,
  MemoryScanResult,
  MemoryScanVerdict,
} from "../types.js";

/**
 * Scan a single memory entry for poisoning.
 *
 * `auth` is the agent that signs the on-chain audit log for the
 * LLM-judge call. Unicode-evasion presence is surfaced to the prompt
 * so the LLM can weight suspicion accordingly.
 */
export async function scanMemory(
  entry: MemoryEntry,
  auth: AgentAuth,
  opts?: MemoryScanOptions,
): Promise<MemoryScanResult> {
  const threshold = opts?.threshold ?? 0.6;
  const request = native.buildScanRequest(entry);

  const atbash = new Atbash(auth.privkey, {
    endpoint: opts?.endpoint,
    verifyPubKey: opts?.verifyPubKey,
    orgName: opts?.orgName,
  });
  const result = await atbash.judgeAction(request.prompt, request.context, {
    toolName: opts?.toolName ?? "memory_write",
    toolArgsJson:
      opts?.toolArgsJson ??
      JSON.stringify({ key: entry.key, source: entry.source }),
    mode: "memory-scan",
  });

  const verdict = native.mapVerdict(
    result.actionType,
    result.confidence,
    threshold,
  ) as MemoryScanVerdict;
  const parsed = native.parseScoreFromReason(result.reason);
  const score =
    result.score ?? parsed.score ?? native.defaultScoreForVerdict(verdict);

  return {
    safe: verdict === "green",
    verdict,
    reason: parsed.clean_reason,
    confidence: result.confidence,
    score,
    toolCallId: result.toolCallId,
  };
}

/**
 * Scan multiple entries. Stops on the first red verdict by default —
 * set `stopOnRed: false` to scan every entry regardless.
 */
export async function scanMemoryBatch(
  entries: MemoryEntry[],
  auth: AgentAuth,
  opts?: MemoryScanOptions & { stopOnRed?: boolean },
): Promise<MemoryScanResult[]> {
  const stopOnRed = opts?.stopOnRed !== false;
  const results: MemoryScanResult[] = [];
  for (const entry of entries) {
    const r = await scanMemory(entry, auth, opts);
    results.push(r);
    if (stopOnRed && r.verdict === "red") break;
  }
  return results;
}
